/**
 * Shared-компонент «Поиск клиента» (`component.client-search`, centrika-docs!43): единое
 * поле поиска по наименованию ИЛИ ИНН клиента с кнопкой сброса-крестиком внутри поля.
 * Экран, который ищет ещё и по другим признакам (очередь заказов — по № заказа), передаёт
 * свои подсказку и доступное имя; правила сброса и фокуса — общие.
 *
 * Компонент управляемый и данных не грузит: фильтрацию выполняет вызывающая сторона
 * (страница «Клиенты» передаёт значение в `GET /api/team/clients?q=`), она же сбрасывает
 * список на первую страницу при изменении запроса.
 */
import { useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/design-system/components/primitives/input'

export interface ClientSearchProps {
  value: string
  onChange: (value: string) => void
  className?: string
  /** Подсказка в пустом поле; по умолчанию — «Поиск по названию или ИНН». */
  placeholder?: string
  /** Доступное имя поля; по умолчанию — «Поиск клиента». */
  ariaLabel?: string
}

export function ClientSearch({
  value,
  onChange,
  className,
  placeholder = 'Поиск по названию или ИНН',
  ariaLabel = 'Поиск клиента',
}: ClientSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  function clear() {
    onChange('')
    // Фокус остаётся в поле поиска (требование): после сброса можно сразу набирать заново.
    inputRef.current?.focus()
  }

  return (
    <div className={cn('relative', className)}>
      <Input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        // Место под крестик — только когда он показан (пустое поле крестика не имеет).
        className={cn(value !== '' && 'pr-10')}
      />
      {value !== '' ? (
        <button
          type="button"
          aria-label="Очистить поиск"
          onClick={clear}
          className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-7 text-txt-secondary outline-none transition-colors hover:text-txt focus-visible:ring-2 focus-visible:ring-accent"
        >
          <X className="size-4 stroke-current" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}
